/**
 * Phone numbers, one house format — MTC-FCS-011.
 * ============================================================================
 * `(###) ###-####`, everywhere a number is shown: the generated website, the
 * invoice designs, and the two forms that collect one.
 *
 * ── IT ONLY REFORMATS WHAT IT RECOGNISES ───────────────────────────────────
 * Ten digits, or eleven beginning with a 1, are formatted. Everything else —
 * an international number, one with an extension, a half-typed one — comes
 * back exactly as it was given. This is the important rule in the file. A
 * phone number is how a customer reaches this business; a formatter that
 * tidies a number it does not actually understand puts a WRONG number on a
 * live website, and nobody finds out until a call fails to arrive. Leaving an
 * unusual number looking unusual is the cheap failure.
 *
 * ── THE LINK AND THE LABEL ARE DIFFERENT STRINGS ───────────────────────────
 * `tel:` wants digits and a person wants brackets, so formatting is for the
 * label only. `telHref` works off the raw value, which means a number this
 * file declines to format still dials correctly.
 */

const digitsOf = value => String(value ?? "").replace(/\D/g, "")

/**
 * The ten significant digits of a North American number, or null.
 *
 * A letter anywhere disqualifies the value: `555-0123 ext 4` and
 * `1-800-FLOWERS` are both numbers a person typed on purpose, and neither
 * survives being squeezed into ten boxes.
 */
export function usDigits(value) {
  const raw = String(value ?? "")
  if (/[a-z]/i.test(raw)) return null

  const d = digitsOf(raw)
  if (d.length === 10) return d
  // A leading 1 is the country code, and +1 is the same country.
  if (d.length === 11 && d[0] === "1") return d.slice(1)
  return null
}

/** `(425) 239-2211` — or the value untouched when it is not a US number. */
export function formatPhone(value) {
  const d = usDigits(value)
  if (!d) return String(value ?? "").trim()
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/**
 * The same format, built up as somebody types it.
 *
 * Brackets appear only once there is a reason for them: an input that answers
 * a single keystroke with `(4` has taken the field over, and the first thing
 * anybody does is delete it. Below four digits this returns the digits alone.
 *
 * Anything that is plainly not a plain US number — a leading `+`, a letter,
 * more digits than a US number has — is handed straight back, so the field
 * never fights somebody entering an international number or an extension.
 */
export function formatPhoneInput(value) {
  const raw = String(value ?? "")
  if (raw.startsWith("+") || /[a-z]/i.test(raw)) return raw

  let d = digitsOf(raw)
  if (d.length === 11 && d[0] === "1") d = d.slice(1)
  if (d.length > 10) return raw

  if (d.length <= 3) return d
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/**
 * A `tel:` href, or null when there is no number.
 *
 * US numbers are dialled in full international form, because a site is read on
 * phones that may not be roaming on the same network the business is on.
 */
export function telHref(value) {
  const raw = String(value ?? "").trim()
  if (!raw) return null

  const us = usDigits(raw)
  if (us) return `tel:+1${us}`

  // Not a number this file claims to understand: keep whatever dialable
  // characters it has rather than dropping the link entirely.
  const kept = raw.replace(/[^\d+]/g, "")
  return kept ? `tel:${kept}` : null
}
